/**
 * Les brouillons du bilan de séance : ce que le masteur a dit sur chaque élève (la note, les
 * étiquettes, le mot, son envoi, « Terminé ») et sur la plateforme, tant que l'écran est ouvert.
 *
 * Semés UNE fois depuis le serveur, à la première lecture de chaque rapport — l'ancien écran
 * gardait la même surcouche locale : l'enregistrement à la frappe ne lui revenait pas, et les
 * relectures du score oral (toutes les 5 s) ne doivent pas écraser ce qu'il est en train d'écrire.
 * « Terminé » est un verrou : semé de ce que le serveur sait, il ne bouge qu'au geste, ou quand le
 * mot qu'il reprenait n'arrive pas au serveur.
 *
 * Pur : le réducteur se joue dans un test, sans écran.
 */
import {
    initialCommentMode,
    isCloseOutDone,
    platformTagsPositive,
    tagKeysOf,
    usableCommentRecord,
    type ApiCloseOutOccurrence,
    type ApiCloseOutReport,
} from "../../../adapters/tutor/Lives/tutorCloseOutAdapter"
import { type UITutorCommentMode } from "../../../ui/types/tutorCloseOut"

/** Où en est un mot côté serveur ; `retry` dit ce que « Réessayer » renvoie. */
export type CloseOutCommentDraft = {
    mode: UITutorCommentMode
    text: string
    voice: { uri: string; durationLabel: string } | null
    untranscribed: boolean
    send: "sending" | "failed" | null
    sendError?: string
    retry?: "text" | "voice"
}

export type CloseOutStudentDraft = CloseOutCommentDraft & { rating: number; tags: string[]; done: boolean }
export type CloseOutPlatformDraft = CloseOutCommentDraft & { rating: number; tags: string[] }

export type CloseOutDrafts = {
    students: Record<string, CloseOutStudentDraft>
    platform: CloseOutPlatformDraft | null
}

/** À qui s'adresse un geste : un élève (l'identifiant de son rapport), ou la plateforme. */
export type CloseOutTarget = { kind: "student"; id: string } | { kind: "platform" }

export const PLATFORM: CloseOutTarget = { kind: "platform" }
export const studentTarget = (id: string | number): CloseOutTarget => ({ kind: "student", id: String(id) })
/** La clé d'une cible : celle des minuteries d'enregistrement et du vocal réécouté. */
export const targetKey = (target: CloseOutTarget) => (target.kind === "platform" ? "platform" : `student:${target.id}`)

export const EMPTY_DRAFTS: CloseOutDrafts = { students: {}, platform: null }

export function seedStudentDraft(report: ApiCloseOutReport): CloseOutStudentDraft {
    const text = report.tutor_comment ?? ""
    const record = usableCommentRecord(report)
    return {
        rating: report.tutor_rating ?? 0,
        tags: tagKeysOf(report.tutor_tags),
        mode: initialCommentMode(text),
        text,
        voice: record != null ? { uri: record, durationLabel: "" } : null,
        // Dit jusqu'à ce qu'il réenregistre ou qu'il écrive.
        untranscribed: !!report.comment_record_untranscribed && text.trim() === "",
        send: null,
        done: isCloseOutDone(report),
    }
}

/** Le ressenti s'ouvre en texte, sauf s'il a déjà laissé un vocal (l'ancienne section). */
function seedPlatformDraft(occurrence: ApiCloseOutOccurrence): CloseOutPlatformDraft {
    const record = occurrence.live_satisfaction_record ?? null
    return {
        rating: occurrence.tutor_live_quality_score ?? 0,
        tags: occurrence.live_satisfaction_tags ?? [],
        mode: record != null ? "voice" : "text",
        text: occurrence.tutor_comment ?? "",
        voice: record != null ? { uri: record, durationLabel: "" } : null,
        untranscribed: false,
        send: null,
    }
}

export type CloseOutDraftAction =
    | { type: "seed"; occurrence: ApiCloseOutOccurrence }
    | { type: "rate"; target: CloseOutTarget; rating: number }
    | { type: "toggleTag"; target: CloseOutTarget; tag: string }
    | { type: "setMode"; target: CloseOutTarget; mode: UITutorCommentMode }
    | { type: "setText"; target: CloseOutTarget; text: string }
    | { type: "voiceRecorded"; target: CloseOutTarget; voice: { uri: string; durationLabel: string } }
    | { type: "deleteVoice"; target: CloseOutTarget }
    | { type: "sendStarted"; target: CloseOutTarget }
    | { type: "sendSucceeded"; target: CloseOutTarget }
    | { type: "sendFailed"; target: CloseOutTarget; message: string; retry: "text" | "voice" }
    | { type: "done"; target: CloseOutTarget }

type AnyDraft = CloseOutStudentDraft | CloseOutPlatformDraft

export function draftOf(drafts: CloseOutDrafts, target: CloseOutTarget): AnyDraft | null {
    return target.kind === "platform" ? drafts.platform : (drafts.students[target.id] ?? null)
}

function update(drafts: CloseOutDrafts, target: CloseOutTarget, change: (draft: AnyDraft) => AnyDraft): CloseOutDrafts {
    const current = draftOf(drafts, target)
    if (current == null) return drafts
    const next = change(current)
    if (next === current) return drafts
    return target.kind === "platform"
        ? { ...drafts, platform: next as CloseOutPlatformDraft }
        : { ...drafts, students: { ...drafts.students, [target.id]: next as CloseOutStudentDraft } }
}

const settled = { send: null, sendError: undefined, retry: undefined } as const

export function closeOutDraftsReducer(drafts: CloseOutDrafts, action: CloseOutDraftAction): CloseOutDrafts {
    switch (action.type) {
        case "seed": {
            const reports = action.occurrence.live_reports ?? []
            const missing = reports.filter((report) => drafts.students[String(report.id)] == null)
            if (missing.length === 0 && drafts.platform != null) return drafts
            const students = { ...drafts.students }
            for (const report of missing) students[String(report.id)] = seedStudentDraft(report)
            return { students, platform: drafts.platform ?? seedPlatformDraft(action.occurrence) }
        }
        case "rate":
            return update(drafts, action.target, (draft) => {
                if (action.target.kind === "student") return { ...draft, rating: action.rating, tags: [] }
                // Le ressenti ne remet ses étiquettes à zéro que si leur polarité change.
                const samePolarity = platformTagsPositive(draft.rating) === platformTagsPositive(action.rating)
                return { ...draft, rating: action.rating, tags: samePolarity ? draft.tags : [] }
            })
        case "toggleTag":
            return update(drafts, action.target, (draft) => ({
                ...draft,
                tags: draft.tags.includes(action.tag)
                    ? draft.tags.filter((tag) => tag !== action.tag)
                    : [...draft.tags, action.tag],
            }))
        case "setMode":
            return update(drafts, action.target, (draft) => {
                if (draft.mode === action.mode) return draft
                // Changer de mode efface ce qui a été dit dans l'autre : un mot est écrit OU oral.
                return {
                    ...draft,
                    ...settled,
                    mode: action.mode,
                    text: draft.mode === "text" ? "" : draft.text,
                    voice: draft.mode === "voice" ? null : draft.voice,
                }
            })
        case "setText":
            return update(drafts, action.target, (draft) => ({ ...draft, text: action.text, untranscribed: false }))
        case "voiceRecorded":
            return update(drafts, action.target, (draft) => ({
                ...draft,
                voice: action.voice,
                untranscribed: false,
                send: "sending",
                sendError: undefined,
            }))
        case "deleteVoice":
            return update(drafts, action.target, (draft) => ({ ...draft, ...settled, voice: null }))
        case "sendStarted":
            return update(drafts, action.target, (draft) => ({ ...draft, send: "sending", sendError: undefined }))
        case "sendSucceeded":
            return update(drafts, action.target, (draft) => (draft.send == null ? draft : { ...draft, ...settled }))
        case "sendFailed":
            return update(drafts, action.target, (draft) => ({
                ...draft,
                send: "failed",
                sendError: action.message,
                retry: action.retry,
                // Un mot qu'il reprenait n'est pas arrivé : la carte faite se rouvre.
                ...("done" in draft ? { done: false } : {}),
            }))
        case "done":
            return update(drafts, action.target, (draft) => ("done" in draft ? { ...draft, done: true } : draft))
    }
}
